import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    EntitiesAccessRightsUpdateScope,
    StandardAccessRight,
} from "api/generated/model";
import { compareAccessRight } from "utils/accessRights";

import { ControlEntityRow } from "./useUserControlEntitiesListTable";

export type RemovableScope =
    | typeof EntitiesAccessRightsUpdateScope.control
    | typeof EntitiesAccessRightsUpdateScope.risk;

export interface RemovalPlan {
    scope: RemovableScope;
    entityIds: number[];
    selectedCount: number;
    accessRights: StandardAccessRight[];
}

function accessRightIn(
    row: ControlEntityRow,
    scope: RemovableScope
): StandardAccessRight | null {
    switch (scope) {
        case EntitiesAccessRightsUpdateScope.control:
            return row.controlAccessRight;
        case EntitiesAccessRightsUpdateScope.risk:
            return row.riskAccessRight;
        default:
            return assertNever(scope);
    }
}

/** A remove action only applies to the selected rows holding a right in its scope. */
export function planRemoval(
    rows: ControlEntityRow[],
    scope: RemovableScope
): RemovalPlan {
    const affected = rows.flatMap((row) => {
        const accessRight = accessRightIn(row, scope);
        return accessRight ? [{ id: row.id, accessRight }] : [];
    });
    return {
        scope,
        entityIds: affected.map(({ id }) => id),
        selectedCount: rows.length,
        accessRights: [
            ...new Set(affected.map(({ accessRight }) => accessRight)),
        ].sort(compareAccessRight),
    };
}

export function removalLabels(t: TFunction, scope: RemovableScope) {
    switch (scope) {
        case EntitiesAccessRightsUpdateScope.control:
            return {
                action: t("AccessAndAssignment_Tool_RemoveControlAccessRight"),
                understand: (user: string, count: number) =>
                    t("AccessAndAssignment_RemoveControlAccessRight_Confirm", {
                        user,
                        count,
                    }),
                removed: (count: number) =>
                    t("AccessAndAssignment_ControlAccessRightRemoved", {
                        count,
                    }),
            };
        case EntitiesAccessRightsUpdateScope.risk:
            return {
                action: t("AccessAndAssignment_Tool_RemoveRiskAccessRight"),
                understand: (user: string, count: number) =>
                    t("AccessAndAssignment_RemoveRiskAccessRight_Confirm", {
                        user,
                        count,
                    }),
                removed: (count: number) =>
                    t("AccessAndAssignment_RiskAccessRightRemoved", { count }),
            };
        default:
            return assertNever(scope);
    }
}
